import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PillIcon, ShieldAlertIcon, RepeatIcon, CheckIcon, PackageIcon, AlertTriangleIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/StatCard';
import { rxQueue, type RxQueueItem } from '../data/operations';
const alertMeta = {
  allergy: {
    icon: ShieldAlertIcon,
    tone: 'danger' as const,
    label: 'Allergy'
  },
  interaction: {
    icon: AlertTriangleIcon,
    tone: 'warning' as const,
    label: 'Interaction'
  },
  duplicate: {
    icon: RepeatIcon,
    tone: 'purple' as const,
    label: 'Duplicate'
  }
};
const statusTone = {
  Review: 'warning',
  Verified: 'info',
  Dispensed: 'success'
} as const;
function RxRow({
  item
}: {
  item: RxQueueItem;
}) {
  const hasBlocking = item.alerts.some(a => a.type === 'allergy');
  return <motion.div layout initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`rounded-lg border bg-white p-4 shadow-card ${hasBlocking ? 'border-red-200' : 'border-slate-200'}`} data-id="element-3835" data-mp="2nqmq3oiw4y7m">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-3836" data-mp="2xr6s4te8i6qw">
        <div className="min-w-0" data-id="element-3837" data-mp="2sphgpcn3nr2a">
          <div className="flex items-center gap-2" data-id="element-3838" data-mp="2sp7pxmu8dfvj">
            <p className="text-sm font-semibold text-slate-900" data-id="element-3839" data-mp="36pih6qzgcwh8">{item.drug}</p>
            {item.priority === 'STAT' && <Badge tone="danger" data-id="element-3840" data-mp="31rz0lkfa87m4">STAT</Badge>}
          </div>
          <p className="text-xs text-slate-500" data-id="element-3841" data-mp="3gg3or3x3tbca">{item.dose} · {item.patient} · {item.ward}</p>
        </div>
        <Badge tone={statusTone[item.status]} data-id="element-3842" data-mp="3bh0m1q4ljh7h">{item.status}</Badge>
      </div>

      {item.alerts.length > 0 && <div className="mt-3 space-y-1.5" data-id="element-3843" data-mp="3pg9zii1f3x7r">
          {item.alerts.map((a, i) => {
        const m = alertMeta[a.type];
        return <div key={i} className={`flex items-start gap-2 rounded-md px-2.5 py-1.5 text-xs ${a.type === 'allergy' ? 'bg-red-50 text-red-700' : a.type === 'interaction' ? 'bg-amber-50 text-amber-700' : 'bg-violet-50 text-violet-700'}`} data-id="element-3844" data-mp="3lh13t7mthp9k" data-mpi={i}>
                <m.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3845" data-mp="6bg61j066fri" />
                <span data-id="element-3846" data-mp="693h2lvzv54y"><span className="font-semibold" data-id="element-3847" data-mp="69bt6bkam6f3">{m.label}:</span> {a.text}</span>
              </div>;
      })}
        </div>}

      {item.status === 'Review' && <div className="mt-3 flex gap-2" data-id="element-3848" data-mp="f38ejo5mvetg">
          {hasBlocking ? <>
              <Button variant="danger" size="sm" data-id="element-3849" data-mp="k105aeujkuw2">Override with reason</Button>
              <Button variant="outline" size="sm" data-id="element-3850" data-mp="p2pj2295fmnu">Contact prescriber</Button>
            </> : <>
              <Button size="sm" icon={<CheckIcon className="h-4 w-4" data-id="element-3852" data-mp="tyelqeggtjod" />} data-id="element-3851" data-mp="u08xb25ujzfs">Verify & dispense</Button>
              <Button variant="ghost" size="sm" data-id="element-3853" data-mp="2quluzcz8ayj5">Hold</Button>
            </>}
        </div>}
    </motion.div>;
}
export function Pharmacy() {
  const [filter, setFilter] = useState<'All' | 'Review'>('Review');
  const list = filter === 'All' ? rxQueue : rxQueue.filter(r => r.status === 'Review');
  return <div className="p-6" data-id="element-3854" data-mp="184g6o708mart">
      <PageHeader title="Pharmacy Dispensing" subtitle="e-Prescription queue · interaction, allergy & duplicate-therapy checking · FEFO dispensing" action={<Button icon={<PackageIcon className="h-4 w-4" data-id="element-3857" data-mp="1vvlnaoe05736" />} data-id="element-3856" data-mp="z3rvrl654qj5">Stock & imprest</Button>} data-id="element-3855" data-mp="1446474bnm2hp" />

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-3858" data-mp="1vtiz89weto13">
        <StatCard label="In queue" value="27" icon={<PillIcon className="h-5 w-5" data-id="element-3860" data-mp="1mv6xpcu095tv" />} accent="text-brand-600 bg-brand-50" footnote="avg wait 9 min" data-id="element-3859" data-mp="1rurylssqjo98" />
        <StatCard label="Blocking alerts" value="2" icon={<ShieldAlertIcon className="h-5 w-5" data-id="element-3862" data-mp="1hvkmf8xqlznp" />} accent="text-red-600 bg-red-50" footnote="Require pharmacist action" data-id="element-3861" data-mp="1hwciekv8xzei" />
        <StatCard label="Near-expiry value" value="₨ 1.2M" icon={<AlertTriangleIcon className="h-5 w-5" data-id="element-3864" data-mp="6gnrxjisai8y" />} accent="text-amber-600 bg-amber-50" footnote="FEFO prioritised" data-id="element-3863" data-mp="bdxg6vufsnv7" />
        <StatCard label="Dispensed today" value="642" delta={{
        value: '4%',
        direction: 'up',
        good: true
      }} icon={<CheckIcon className="h-5 w-5" data-id="element-3866" data-mp="1z5wnm5gkjkt1" />} accent="text-emerald-600 bg-emerald-50" data-id="element-3865" data-mp="1uctshsw583aq" />
      </div>

      <Card className="p-4" data-id="element-3867" data-mp="1lei3fvaxddke">
        <div className="mb-4 flex items-center justify-between" data-id="element-3868" data-mp="1lb3ziimm4wau">
          <h3 className="text-sm font-semibold text-slate-900" data-id="element-3869" data-mp="1qcta5x5xb7ye">Prescription Review Queue</h3>
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1" data-id="element-3870" data-mp="2e2xhaeiblpu5">
            {(['Review', 'All'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-3871" data-mp="2i0v6hhc3cbxj">
                {f === 'Review' ? 'Needs review' : 'All'}
              </button>)}
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2" data-id="element-3872" data-mp="q3tlvsnyo3c6">
          {list.map(item => <RxRow key={item.id} item={item} data-id="element-3873" data-mp="q5obhka6gjml" />)}
        </div>
      </Card>
    </div>;
}